import { PropsWithChildren } from 'react';

import { MeetModerationBridgeApi, MeetModerationProvider } from '../../features/meet/MeetModerationProvider';
import { MeetOverlaysBridgeApi, MeetOverlaysProvider } from '../../features/meet/MeetOverlaysProvider';
import { MeetPermissionsProvider } from '../../features/meet/MeetPermissionsProvider';
import { MeetToolsBridgeApi, MeetToolsProvider } from '../../features/meet/MeetToolsProvider';
import type { RemoteVideoLike } from '../../features/meet/types';
import { useMeetPermissions } from '../../features/meet/useMeetPermissions';
import { IClient, IConsultation, IJournalResult } from '../../store/types';

interface MeetProviderTreeBaseProps {
  roomName: string;
  consultation?: IConsultation;
  client?: IClient;
  isGroupConsultationFlag: boolean;
  isPsychologist: boolean;
  localParticipantIdentity?: string;
  remoteVideos: RemoteVideoLike[];
  sendConnectionAllowed?: (id: string) => void;
  audioDevice?: MediaDeviceInfo;
  videoDevice?: MediaDeviceInfo;
  toggleAudio?: () => boolean | void | Promise<boolean | void>;
  toggleVideo?: () => boolean | void | Promise<boolean | void>;
  permissionsValue: ReturnType<typeof useMeetPermissions>;
  onParticipantNamesChange: (names: Record<string, string>) => void;
  onModerationApi: (api: MeetModerationBridgeApi) => void;
  onOverlaysApi: (api: MeetOverlaysBridgeApi) => void;
}

interface MeetSceneProviderTreeProps extends MeetProviderTreeBaseProps {
  result: IJournalResult;
  setResult: (value: IJournalResult) => void;
  handleToolActivated: (toolId: string, properties?: Record<string, unknown>) => void;
  onToolsApi: (api: MeetToolsBridgeApi) => void;
}

export function MeetBaseProviderTree({
  children,
  roomName,
  consultation,
  client,
  isGroupConsultationFlag,
  isPsychologist,
  localParticipantIdentity,
  remoteVideos,
  sendConnectionAllowed,
  audioDevice,
  videoDevice,
  toggleAudio,
  toggleVideo,
  permissionsValue,
  onParticipantNamesChange,
  onModerationApi,
  onOverlaysApi,
}: PropsWithChildren<MeetProviderTreeBaseProps>) {
  return (
    <MeetModerationProvider
      roomName={roomName}
      isGroupConsultationFlag={isGroupConsultationFlag}
      isPsychologist={isPsychologist}
      localParticipantIdentity={localParticipantIdentity}
      sendConnectionAllowed={sendConnectionAllowed}
      remoteVideos={remoteVideos}
      psychologistId={consultation?.psychologist?.id}
      onParticipantNamesChange={onParticipantNamesChange}
      onApi={onModerationApi}
    >
      <MeetPermissionsProvider
        roomName={roomName}
        audioDevice={audioDevice}
        videoDevice={videoDevice}
        toggleAudio={toggleAudio}
        toggleVideo={toggleVideo}
        value={permissionsValue}
      >
        <MeetOverlaysProvider onApi={onOverlaysApi}>{children}</MeetOverlaysProvider>
      </MeetPermissionsProvider>
    </MeetModerationProvider>
  );
}

export function MeetSceneProviderTree({
  children,
  result,
  setResult,
  handleToolActivated,
  onToolsApi,
  ...baseProps
}: PropsWithChildren<MeetSceneProviderTreeProps>) {
  return (
    <MeetBaseProviderTree {...baseProps}>
      <MeetToolsProvider
        result={result}
        setResult={setResult}
        handleToolActivated={handleToolActivated}
        onApi={onToolsApi}
      >
        {children}
      </MeetToolsProvider>
    </MeetBaseProviderTree>
  );
}
